import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCheckCircle, FiClock, FiLayers, FiSlash, FiSearch } from 'react-icons/fi';
import ReservationList from './modules/ReservationList';
import './BookingSection.css';
import '../company/CompanyForm.css';
import {
  listReservations,
  setReservationStatus,
} from '../../../services/reservationsApi';
import { getUnitById, updateUnit } from '../../../services/unitsApi';
import { setPublicationStatus } from '../../../services/publicationsApi';
import { normalizeAvailabilityRanges } from '../../../utils/availabilityRanges';

const TAB_OPTIONS = [
  { key: 'all', label: 'Todos', statuses: null },
  { key: 'pending', label: 'Pendientes', statuses: ['pending'] },
  { key: 'accepted', label: 'Aceptadas', statuses: ['accepted'] },
  { key: 'finalized', label: 'Confirmadas / Canceladas', statuses: ['confirmed', 'rejected', 'cancelled'] },
];

const STATUS_FILTERS = TAB_OPTIONS.reduce((acc, tab) => {
  acc[tab.key] = tab.statuses;
  return acc;
}, {});

const DATE_KEY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const toDateKey = value => {
  if (!value) return '';
  if (typeof value === 'string') {
    const cleaned = value.trim().slice(0, 10);
    if (DATE_KEY_REGEX.test(cleaned)) {
      return cleaned;
    }
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dateFromKey = key => {
  if (!DATE_KEY_REGEX.test(key)) return null;
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const expandRangeIntoDailySlots = (startKey, endKey) => {
  const startDate = dateFromKey(startKey);
  const endDate = dateFromKey(endKey);
  if (!startDate || !endDate) return [];
  const result = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    const currentKey = toDateKey(cursor);
    if (!currentKey) break;
    result.push({ start: currentKey, end: currentKey });
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
};

const sanitizeAvailabilityRanges = ranges => {
  if (!Array.isArray(ranges)) return [];
  return ranges
    .map(range => {
      const start = toDateKey(range?.start);
      const end = toDateKey(range?.end || range?.start);
      if (!start) return null;
      return { start, end: end || start };
    })
    .filter(Boolean);
};

async function blockUnitAvailabilityForReservation(reservation) {
  const unitId = reservation?.unitId || reservation?.unit?.id || '';
  if (!unitId) return;

  const startKey = toDateKey(reservation?.requestedFrom);
  if (!startKey) return;
  const endKey = toDateKey(reservation?.requestedTo) || startKey;

  try {
    const unit = await getUnitById(unitId);
    const existing = sanitizeAvailabilityRanges(unit?.availabilityRanges);
    const dayEntries = expandRangeIntoDailySlots(startKey, endKey);
    if (!dayEntries.length) return;

    const seen = new Set(existing.map(range => `${range.start}-${range.end || range.start}`));
    let added = false;
    dayEntries.forEach(range => {
      const key = `${range.start}-${range.end}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      existing.push(range);
      added = true;
    });

    if (!added) return;

    const normalized = normalizeAvailabilityRanges(existing);
    await updateUnit(unitId, { availabilityRanges: normalized });
  } catch (error) {
    console.error('No se pudo actualizar la disponibilidad de la unidad para la reserva aceptada.', error);
  }
}

const RESERVATIONS_KPI_STORAGE_KEY = 'flotte:reservationsKpiCollapsed';

export default function BookingSection({ empresaId }) {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab] = useState('all');
  const [notes, setNotes] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [processing, setProcessing] = useState({});
  const numberFormatter = useMemo(() => new Intl.NumberFormat('es-MX'), []);
  const [kpiCollapsed, setKpiCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(RESERVATIONS_KPI_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const fetchReservations = useCallback(async () => {
    if (!empresaId) {
      setReservations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await listReservations({ companyId: empresaId, limit: 200 });
      setReservations(data);
    } catch (err) {
      setError(err?.message || 'No fue posible cargar las reservas del marketplace.');
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        RESERVATIONS_KPI_STORAGE_KEY,
        kpiCollapsed ? 'true' : 'false'
      );
    } catch {
      // ignore
    }
  }, [kpiCollapsed]);

  const summaries = useMemo(() => {
    const base = {
      total: reservations.length,
      pending: 0,
      accepted: 0,
      confirmed: 0,
      rejected: 0,
      cancelled: 0,
    };
    reservations.forEach(item => {
      const statusKey = item.status || 'pending';
      if (base[statusKey] !== undefined) {
        base[statusKey] += 1;
      }
    });
    base.all = base.total;
    base.finalized = base.confirmed + base.rejected + base.cancelled;
    return base;
  }, [reservations]);

  const filteredReservations = useMemo(() => {
    const targetStatuses = STATUS_FILTERS[activeTab];
    let dataset = reservations;
    if (Array.isArray(targetStatuses) && targetStatuses.length) {
      dataset = dataset.filter(item => targetStatuses.includes(item.status));
    }
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return dataset;
    }
    return dataset.filter(item => {
      const fields = [
        item.id,
        item.publication?.title,
        item.publication?.name,
        item.unit?.model,
        item.unit?.economicNumber,
        item.unit?.plates,
        item.contactName,
        item.contactEmail,
        item.routeDescription,
      ];
      return fields.some(value => {
        if (value === null || typeof value === 'undefined') return false;
        return value.toString().toLowerCase().includes(query);
      });
    });
  }, [activeTab, reservations, searchQuery]);

  const reservationKpis = useMemo(() => {
    const formatted = value => numberFormatter.format(value || 0);
    return [
      {
        id: 'pending',
        label: 'Pendientes',
        value: formatted(summaries.pending),
        context: 'En espera de accion',
        icon: FiClock,
        variant: 'warning',
      },
      {
        id: 'accepted',
        label: 'Aceptadas',
        value: formatted(summaries.accepted),
        context: 'Programadas con tu equipo',
        icon: FiCheckCircle,
        variant: 'success',
      },
      {
        id: 'finalized',
        label: 'Confirmadas / Canceladas',
        value: formatted(summaries.confirmed + summaries.rejected + summaries.cancelled),
        context: 'Viajes cerrados o cancelados',
        icon: FiSlash,
        variant: 'danger',
      },
      {
        id: 'total',
        label: 'Total historico',
        value: formatted(summaries.total),
        context: 'Solicitudes acumuladas',
        icon: FiLayers,
        variant: 'info',
      },
    ];
  }, [numberFormatter, summaries.accepted, summaries.cancelled, summaries.confirmed, summaries.pending, summaries.rejected, summaries.total]);

  const handleNotesChange = useCallback((id, value) => {
    setNotes(prev => ({
      ...prev,
      [id]: value,
    }));
  }, []);

  const handleDecision = useCallback(
    async (reservation, nextStatus) => {
      if (!reservation?.id) return;
      setProcessing(prev => ({
        ...prev,
        [reservation.id]: nextStatus,
      }));
      setError('');

      try {
        const decisionNotes =
          Object.prototype.hasOwnProperty.call(notes, reservation.id) && notes[reservation.id] !== undefined
            ? notes[reservation.id]
            : reservation.decisionNotes || '';

        const updated = await setReservationStatus(reservation.id, nextStatus, {
          decisionNotes,
        });

        setReservations(prev =>
          prev.map(item => (item.id === reservation.id ? { ...item, ...updated } : item))
        );

        setNotes(prev => {
          const clone = { ...prev };
          if (clone[reservation.id] !== undefined) {
            delete clone[reservation.id];
          }
          return clone;
        });

        if (nextStatus === 'accepted') {
          await blockUnitAvailabilityForReservation(updated);
          const publicationId = updated.publicationId || updated.publication?.id || '';
          if (publicationId) {
            await setPublicationStatus(publicationId, 'paused');
          }
        } else if (nextStatus === 'confirmed') {
          // No acciones adicionales por ahora; se dispara la encuesta desde el backend.
        }
      } catch (decisionError) {
        console.error('Error al actualizar la reserva.', decisionError);
        setError(
          decisionError?.message ||
            'No fue posible actualizar la reserva. Intenta nuevamente en unos minutos.'
        );
      } finally {
        setProcessing(prev => {
          const clone = { ...prev };
          delete clone[reservation.id];
          return clone;
        });
      }
    },
    [notes]
  );

  const baseEmptyMessage = empresaId
    ? 'No hay reservas con este estado.'
    : 'Asocia tu empresa para comenzar a recibir reservas desde las publicaciones.';
  const searchActive = Boolean(searchQuery.trim());
  const emptyMessage = searchActive
    ? `No encontramos reservas que coincidan con "${searchQuery.trim()}".`
    : baseEmptyMessage;

  const handleReservationPatched = useCallback(updatedRecord => {
    if (!updatedRecord?.id) return;
    setReservations(prev =>
      prev.map(item => (item.id === updatedRecord.id ? { ...item, ...updatedRecord } : item))
    );
  }, []);

  return (
    <div className="reservations-shell reservations-shell--dashboard">
      <div className="reservations-layout">
        <div className={`dashboard-kpi-wrapper${kpiCollapsed ? ' is-collapsed' : ''}`}>
          <div className="dashboard-kpi-wrapper__controls">
            <span className="dashboard-kpi-wrapper__label">Indicadores</span>
            <button
              type="button"
              className="dashboard-kpi-toggle"
              onClick={() => setKpiCollapsed(prev => !prev)}
              aria-pressed={!kpiCollapsed}
            >
              {kpiCollapsed ? 'Mostrar' : 'Ocultar'}
            </button>
          </div>
          {!kpiCollapsed ? (
            <section className="reservations-kpi-grid" aria-label="Indicadores de reservas">
              {reservationKpis.map(card => {
                const Icon = card.icon;
                return (
                  <article key={card.id} className="reservations-kpi-card">
                    <span
                      className={`reservations-kpi-card__icon reservations-kpi-card__icon--${card.variant}`}
                      aria-hidden="true"
                    >
                      <Icon />
                    </span>
                    <div className="reservations-kpi-card__body">
                      <p className="reservations-kpi-card__label">{card.label}</p>
                      <strong
                        className={`reservations-kpi-card__value reservations-kpi-card__value--${card.variant}`}
                      >
                        {card.value}
                      </strong>
                      <span className="reservations-kpi-card__context">{card.context}</span>
                    </div>
                  </article>
                );
              })}
            </section>
          ) : null}
        </div>
        <div className="reservations-table-card">
          <div className="reservations-toolbar">
            <div className="reservations-toolbar__context">
              <div className="reservations-toolbar__heading">
                <h2 className="reservations-toolbar__title">Reservas</h2>
              </div>
              <div className="reservations-toolbar__search">
                <FiSearch aria-hidden="true" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  placeholder="Buscar por ID, cliente o unidad..."
                  aria-label="Buscar reservas"
                />
              </div>
            </div>
            <div className="reservations-toolbar__actions">
              <button
                type="button"
                className="reservations-toolbar__button"
                onClick={fetchReservations}
                disabled={loading || !empresaId}
              >
                {loading ? 'Sincronizando' : 'Actualizar'}
              </button>
            </div>
          </div>
          <div className="reservations-table-card__body">
            {error ? <div className="reservations-error">{error}</div> : null}
            <ReservationList
              items={filteredReservations}
              loading={loading}
              onDecision={handleDecision}
              onNotesChange={handleNotesChange}
              notes={notes}
              processing={processing}
              emptyMessage={emptyMessage}
              onReservationUpdate={handleReservationPatched}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
